import { InputAdornment } from '@material-ui/core'
import { DISTRIBUTOR_FILTER_KEY_V2 } from 'elements/hardwareFilter/distributor/constants'
import useComponentsV2List from 'elements/hardwareSelectorV2/fetch/useComponentsV2List'
import BasicAutocompleteSearchInput from 'elements/input/BasicAutocompleteSearchInput'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { debounce } from 'lodash'
import { BaseTheme, SearchOutlineIcon, styled, Typography } from 'opensolar-ui'
import BrowseComponentsDropdownFooter from 'projectSections/sections/hardware/bom/BrowseComponentsDropdownFooter'
import { useTranslate } from 'ra-core'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActionType } from 'types/tracking'
import { ComponentLikeType } from '../../../elements/hardwareSelectorV2/types'
import {
  formatDistributorsApiFilter,
  formatTypeaheadProductMeta,
  formatTypeaheadProductTitle,
  getTypeaheadProductMetaParts,
  pickPreferredDistributorData,
  sanitizeDistributorFilterKeys,
} from './hardwareSearchTypeaheadUtils'
import SearchBarEndAdornment from './SearchBarEndAdornment'

export const TypeDiv = styled('div')`
  margin-left: 10px;
  color: #0a090b;
`

export const ResultWrapper = styled('div')`
  display: flex;
  align-items: center;
  overflow: hidden;
  fontsize: 14;
`

export const Code = styled('span')`
  color: #7f7d83;
`

export const Description = styled('div')`
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 10px;
  color: #0a090b;
`

const BomResultWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`

const MetaParts = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}))

const MetaPart = styled('span')(({ theme }) => ({
  '&:not(:last-child)::after': {
    content: `" · "`,
    margin: '0 0.25em',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    '&:not(:last-child)::after': {
      content: 'none',
    },
  },
}))

const BomResultTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  display: 'block',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  [theme.breakpoints.up('sm')]: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}))

const BomResultMeta = styled(Typography)({
  width: '100%',
  display: 'block',
  lineHeight: 1.3,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
})

type ApiListFilters = {
  search: string
  limit: number
  referrer?: string
  fieldset?: string
  count_method?: string
  [DISTRIBUTOR_FILTER_KEY_V2]?: string
}

/** Short-lived in-component cache for the Supplier Assist dropdown so that re-applying the same
 *  query while on the page (e.g. clearing then re-typing the same code, or re-opening the default
 *  list) is served locally instead of re-hitting the endpoint. react-admin's store keeps the rows
 *  but still background-refetches on every filterKey change since the dataProvider sets no
 *  `validUntil`. Kept short to bound staleness after activations / price changes. */
const LIST_CACHE_TTL_MS = 60_000

type CachedList = { data: ComponentLikeType[]; expires: number }

const HardwareSearchInputV3 = ({
  onSelect,
  onEnter,
  variant = 'standard',
  isShop3,
  location = 'shop',
  showSuggestion,
  setShowSuggestion,
  distributors,
  onBrowse,
  onAddCustomItem,
}: {
  onEnter?(value: string): void
  onSelect?(item: unknown): void
  variant?: 'standard' | 'outlined'
  isShop3?: boolean
  location?: 'shop' | 'bom' | 'review_quote'
  showSuggestion: boolean
  setShowSuggestion: (value: boolean) => void
  distributors?: string[]
  onBrowse?: () => void
  onAddCustomItem?: () => void
}) => {
  const isBomOrReviewQuote = ['bom', 'review_quote'].includes(location)
  const autoFocus = isBomOrReviewQuote
  const translate = useTranslate()
  const [searchString, setSearchString] = useState('')
  /** When null, list fetch is blocked (cleared search). When set, drives store-backed GET_LIST. */
  const [apiListFilters, setApiListFilters] = useState<ApiListFilters | null>(null)
  const [isSearchPending, setIsSearchPending] = useState(false)
  /** True from the moment new filters are applied until the list hook actually starts loading them.
   *  useQueryWithStore's loading/loaded come from local state that lags the filterKey change by one
   *  render, so for that render it still reports the *previous* query as loaded — which made the old
   *  results flash for a frame while typing. This latch keeps the loading row up across that gap. */
  const [isApplyingResults, setIsApplyingResults] = useState(false)
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'hardware_search_input_v3',
    eventName: 'Hardware Navigation Button',
  })

  /** Scoped (bom / review-quote only) cache of applied-filter key → rows. */
  const listCacheRef = useRef<Map<string, CachedList>>(new Map())
  const getFreshCached = useCallback(
    (key: string | null): ComponentLikeType[] | null => {
      if (!isBomOrReviewQuote || !key) return null
      const entry = listCacheRef.current.get(key)
      return entry && entry.expires > Date.now() ? entry.data : null
    },
    [isBomOrReviewQuote]
  )

  /** Key of the last applied filters, so the loading latch only trips on a real change. Re-applying
   *  the same query (e.g. clearing back to an already-cached empty list) must not flip loading on,
   *  or it would never clear since the hook won't run a fresh fetch for an unchanged filterKey. */
  const lastAppliedKeyRef = useRef<string | null>(null)
  const setListFilters = useCallback(
    (next: ApiListFilters | null) => {
      const nextKey = next ? JSON.stringify(next) : null
      if (nextKey === lastAppliedKeyRef.current) return
      lastAppliedKeyRef.current = nextKey
      setApiListFilters(next)
      // Fresh cache hit renders synchronously, so don't arm the loading bridge for it.
      setIsApplyingResults(next !== null && getFreshCached(nextKey) === null)
    },
    [getFreshCached]
  )
  const setListFiltersRef = useRef(setListFilters)
  setListFiltersRef.current = setListFilters

  const distributorsKey = sanitizeDistributorFilterKeys(distributors).slice().sort().join(',')
  const distributorsFilterValue = useMemo(
    () => formatDistributorsApiFilter(distributorsKey ? distributorsKey.split(',') : undefined),
    [distributorsKey]
  )

  const buildFilters = useCallback(
    (search: string, limit: number = 10): ApiListFilters => {
      const next: ApiListFilters = { search, limit, count_method: 'estimated' }
      if (location === 'shop') {
        next.referrer = 'shop'
        next.fieldset = 'list'
      } else {
        // BOM / review-quote dropdown: lean payload (no per-distributor fat ordering_dict),
        // so the list loads fast and selection needs no follow-up fetch.
        next.fieldset = 'autocomplete'
      }
      if (distributorsFilterValue) {
        next[DISTRIBUTOR_FILTER_KEY_V2] = distributorsFilterValue
      }
      return next
    },
    [location, distributorsFilterValue]
  )

  const debouncedApplySearch = useMemo(
    () =>
      debounce((search: string) => {
        const limit = isBomOrReviewQuote ? 5 : 10
        setListFiltersRef.current(buildFilters(search, limit))
        setIsSearchPending(false)
      }, 300),
    [isBomOrReviewQuote, buildFilters]
  )

  useEffect(() => {
    if (!searchString) {
      debouncedApplySearch.cancel()
      setIsSearchPending(false)
      // BOM / review-quote keep the default list warm (and visible on focus); clearing the text
      // returns to it. Preloading on mount means focus hits the store cache instead of a spinner.
      setListFilters(isBomOrReviewQuote ? buildFilters('', 5) : null)
      return
    }
    setIsSearchPending(true)
    debouncedApplySearch(searchString)
    return () => {
      debouncedApplySearch.cancel()
    }
  }, [searchString, debouncedApplySearch, isBomOrReviewQuote, showSuggestion, buildFilters, setListFilters])

  const listFilterValues: Record<string, unknown> = useMemo(
    () => (apiListFilters ? { ...apiListFilters } : {}),
    [apiListFilters]
  )

  const perPage = typeof apiListFilters?.limit === 'number' ? apiListFilters.limit : 10

  const cacheKey = apiListFilters ? JSON.stringify(apiListFilters) : null
  const cachedData = getFreshCached(cacheKey)
  const cacheHit = cachedData !== null

  const {
    componentsData: hookComponentsData,
    loading: listLoading,
    loaded: listLoaded,
    error: listError,
  } = useComponentsV2List({
    filterValues: listFilterValues,
    perPage,
    // Supplier Assist dropdown: order by "my list" then code (lighter than _default_rule, which
    // also boosts showcase/in-stock and pays for the heavier precompute + filesort).
    sortField: isBomOrReviewQuote ? '_my_list_code' : '_default_rule',
    // Fresh local cache hit → skip the endpoint entirely and render from the cache.
    blockRequest: apiListFilters === null || cacheHit,
  })

  /** useQueryWithStore keeps loading true when blockRequest skips the fetch; hide that in the UI. */
  const listLoadingForUi = apiListFilters !== null && !cacheHit && listLoading
  /** Results for the applied filters are not ready yet. `loaded` includes row hydration, so this also
   *  covers react-admin's "ids cached but rows TTL-evicted" gap — without it that race renders an
   *  empty list as "No results" instead of a loading row. The stale-render flash that this gate would
   *  otherwise allow on apply is covered by the isApplyingResults latch below. */
  const currentResultsNotReady = apiListFilters !== null && !cacheHit && !listLoaded

  // Once the list hook actually reports it is loading the applied filters, hand the loading state
  // over to it and drop the bridge latch (the stale-render gap is now covered by listLoadingForUi).
  useEffect(() => {
    if (isApplyingResults && listLoadingForUi) {
      setIsApplyingResults(false)
    }
  }, [isApplyingResults, listLoadingForUi])

  // Remember the key we actually saw the hook load, so a fetch's result is only ever cached under the
  // key it belongs to (on the apply render the hook still reports the previous query as loaded).
  const loadedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (listLoadingForUi) loadedKeyRef.current = cacheKey
  }, [listLoadingForUi, cacheKey])

  // Cache the rows once a fetch for the current key has settled and hydrated (listLoaded includes
  // hydration), so the same query while on the page is served locally next time. Guarded so unhydrated
  // empties and stale-render data are never cached, and errors retry instead of caching empties.
  useEffect(() => {
    if (
      isBomOrReviewQuote &&
      cacheKey &&
      !cacheHit &&
      !listError &&
      !listLoading &&
      listLoaded &&
      loadedKeyRef.current === cacheKey
    ) {
      listCacheRef.current.set(cacheKey, { data: hookComponentsData, expires: Date.now() + LIST_CACHE_TTL_MS })
    }
  }, [isBomOrReviewQuote, cacheKey, cacheHit, listError, listLoading, listLoaded, hookComponentsData])

  const componentsDataList = cachedData ?? (apiListFilters ? hookComponentsData : [])

  const handleChange = (value: string) => {
    setSearchString(value)
  }

  const onFirstTouch = () => {
    trackUserInteraction({ type: ActionType.EDIT }, { action: 'typed', context: 'shop_search_bar' })
  }

  const handleSelect = useCallback(
    (item: ComponentLikeType) => {
      onSelect?.(item)
      setSearchString('')
      trackUserInteraction({ type: ActionType.SELECT }, { action: 'selected', context: 'shop_search_bar' })
    },
    [onSelect, trackUserInteraction]
  )

  const handleEnter = useCallback(
    (value: string) => {
      onEnter?.(value)
      trackUserInteraction({ type: ActionType.SUBMIT }, { action: 'entered', context: 'shop_search_bar' })
    },
    [onEnter, trackUserInteraction]
  )

  const closeSuggestion = useCallback(() => {
    setShowSuggestion(false)
  }, [setShowSuggestion])

  useEffect(() => {
    if (!isBomOrReviewQuote || !showSuggestion) return

    // Close suggestion when scrolling outside of the suggestion list
    // Only for BOM or Review Quote
    const handleScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Element && target.closest('[data-typeahead-panel]')) {
        return
      }
      closeSuggestion()
      const active = document.activeElement
      if (active instanceof HTMLElement) {
        active.blur()
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isBomOrReviewQuote, showSuggestion, closeSuggestion])

  const handleFocus = useCallback(() => {
    // Filters for the empty/default list are already applied (and preloaded) by the effect above,
    // so focusing just opens the suggestion list — typically served straight from the store cache.
    setShowSuggestion(true)
  }, [setShowSuggestion])

  const handleIconClick = useCallback(() => {
    onEnter?.(searchString)
    closeSuggestion()
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        action: 'clicked',
        context: 'shop_search_bar',
      }
    )
  }, [onEnter, closeSuggestion, searchString, trackUserInteraction])

  const renderer = (record: ComponentLikeType) => {
    const distributorData = pickPreferredDistributorData(record.ordering_v2, distributors)

    if (isBomOrReviewQuote) {
      const title = formatTypeaheadProductTitle(record, distributorData)
      const metaParts = getTypeaheadProductMetaParts(record, distributorData)
      const metaTitle = formatTypeaheadProductMeta(record, distributorData)
      return (
        <BomResultWrapper>
          <BomResultTitle textVariant="body1SemiBold" colorHex={BaseTheme.palette.grey[1300]} title={title}>
            {title}
          </BomResultTitle>
          {metaParts.length > 0 && (
            <BomResultMeta textVariant="caption2" colorHex={BaseTheme.palette.grey[700]} title={metaTitle}>
              <MetaParts>
                {metaParts.map((part) => (
                  <MetaPart key={part}>{part}</MetaPart>
                ))}
              </MetaParts>
            </BomResultMeta>
          )}
        </BomResultWrapper>
      )
    }

    const code = record.code
    const manufacturerOrName = record.manufacturer_name || record.name
    const description = distributorData?.short_description || distributorData?.title || ''
    return (
      <ResultWrapper>
        <Code>{code}</Code>
        {manufacturerOrName ? <TypeDiv>{manufacturerOrName}</TypeDiv> : null}
        {description && <Description>{description}</Description>}
      </ResultWrapper>
    )
  }

  const options: ComponentLikeType[] = isBomOrReviewQuote
    ? (componentsDataList?.filter((cd) => cd.id && cd.code) ?? [])
    : (componentsDataList?.filter((cd) => cd.id && cd.code && cd.ordering_v2?.length) ?? [])

  // Show only the loading row (nothing clickable) until the current search's results are ready:
  // while typing (pending), across the apply→hook handover (applying latch), while the request is in
  // flight, and until the rows are hydrated (currentResultsNotReady). The latch stops the previous
  // results flashing for a frame on each apply; the hydration gate stops the empty "No results" flash.
  const loading = isSearchPending || isApplyingResults || listLoadingForUi || currentResultsNotReady

  const dropdownFooter =
    onBrowse != null ? (
      <BrowseComponentsDropdownFooter onBrowse={onBrowse} onAddCustomItem={onAddCustomItem} />
    ) : undefined

  return (
    <BasicAutocompleteSearchInput
      value={searchString}
      showSuggestion={showSuggestion}
      autoFocus={autoFocus}
      richTypeahead={isBomOrReviewQuote}
      fullWidth={true}
      placeholder={
        isBomOrReviewQuote
          ? translate('Input component code')
          : translate('Find products by name, code, category, or brand')
      }
      loading={loading}
      renderer={renderer}
      options={options}
      InputProps={{
        disableUnderline: true,
        inputProps: {
          autoComplete: 'off',
        },
        endAdornment: isBomOrReviewQuote ? null : (
          <SearchBarEndAdornment loading={listLoadingForUi} onSearchIconClicked={handleIconClick} shop3={isShop3} />
        ),
        startAdornment: isBomOrReviewQuote ? null : (
          <InputAdornment position="start">
            <SearchOutlineIcon color={BaseTheme.palette.grey[700]} />
          </InputAdornment>
        ),
        style: {
          border: `1px solid ${BaseTheme.palette.neutralGrey[600]}`,
          borderRadius: '10px',
        },
      }}
      closeSuggestion={closeSuggestion}
      onFirstTouch={onFirstTouch}
      onSelect={handleSelect}
      onChange={handleChange}
      onEnter={handleEnter}
      onFocus={handleFocus}
      variant={variant}
      shop3={isShop3}
      dropdownFooter={dropdownFooter}
    />
  )
}

export default memo(HardwareSearchInputV3)
